/**
 * Atomic island plots for COL-009.
 *
 * One legacy-cell-sized (2.4 x 2.4) lattice, clipped to the authoritative
 * 0.48-subcell canvas. Ownership IDs are stable (`plot_{col}_{row}`) so a
 * future authorized single-plot unlock source can grant one plot without
 * changing geometry. No ad SDK is implemented here.
 */

import { ENTRANCE, PARCEL_EAST, PARCEL_NORTH, PARCEL_START, PATH_CELLS, SLOTS } from '../config/map';
import {
  canvasSubcells,
  parcelForSubcell,
  plotIdForLegacyCell,
  plotIdForSubcell,
  queryBuildableSubcell,
} from './buildableArea';
import {
  FOUNDATION_DIVISION,
  createSubgridSpec,
  legacyGridToWorld,
  subcellWorldCenter,
  worldToSubgrid,
  type SubgridCoord,
  type WorldPoint,
} from './subgrid';

const SPEC = createSubgridSpec(FOUNDATION_DIVISION);

export const PLOT_ID_RE = /^plot_-?\d+_-?\d+$/;

/** Former east+north park-score total. Split across paid plots so score does not explode. */
export const LAND_SCORE_BUDGET = 26;

export type PlotUnlockSource = 'coins' | 'future_authorized';

export interface IslandPlot {
  id: string;
  col: number;
  row: number;
  legacyMacro: typeof PARCEL_START | typeof PARCEL_EAST | typeof PARCEL_NORTH;
  startsUnlocked: boolean;
  ring: number;
  cost: number;
  parkScore: number;
  subcells: SubgridCoord[];
  center: WorldPoint;
  width: number;
  depth: number;
}

export function isPlotId(id: string): boolean {
  return PLOT_ID_RE.test(id);
}

function parsePlotId(id: string): { col: number; row: number } | null {
  const match = /^plot_(-?\d+)_(-?\d+)$/.exec(id);
  if (!match) {
    return null;
  }
  return { col: Number(match[1]), row: Number(match[2]) };
}

const STARTER_CELL_KEYS = new Set<string>([
  ...SLOTS.filter((slot) => slot.parcelId === PARCEL_START).map((slot) => `${slot.col},${slot.row}`),
  ...PATH_CELLS.filter((cell) => cell.col !== ENTRANCE.col || cell.row !== ENTRANCE.row).map(
    (cell) => `${cell.col},${cell.row}`,
  ),
]);

function isStarterCell(col: number, row: number): boolean {
  return STARTER_CELL_KEYS.has(`${col},${row}`);
}

const EAST_SLOT_PLOT_IDS = new Set(
  SLOTS.filter((slot) => slot.parcelId === PARCEL_EAST).map((slot) => plotIdForLegacyCell(slot.col, slot.row)),
);
const NORTH_SLOT_PLOT_IDS = new Set(
  SLOTS.filter((slot) => slot.parcelId === PARCEL_NORTH).map((slot) => plotIdForLegacyCell(slot.col, slot.row)),
);

/** Designated early-east anchors. Both are required for East macro entitlement (~1800). */
export const EAST_MACRO_ANCHOR_PLOT_IDS = ['plot_3_1', 'plot_4_1'] as const;

/**
 * Ring-cost table. Ring 0 is the starter cluster (free).
 * Nearby ring-1 plots sum near the former 1800 east band.
 * Ring 3–4 (north / former coaster approach) sits in the 9000 band.
 * Outer rings keep climbing for long-tail customization.
 */
export const PLOT_RING_COST = [0, 300, 600, 2400, 9000, 12000, 16000, 21000] as const;

const EAST_SLOT_PLOT_COST = 900;
const NORTH_SLOT_BASE_COST = 9000;

function costForRing(ring: number): number {
  if (ring <= 0) {
    return 0;
  }
  if (ring < PLOT_RING_COST.length) {
    return PLOT_RING_COST[ring]!;
  }
  return PLOT_RING_COST[PLOT_RING_COST.length - 1]! + (ring - (PLOT_RING_COST.length - 1)) * 4000;
}

let cachedPlots: IslandPlot[] | null = null;
let cachedById: Map<string, IslandPlot> | null = null;

function buildPlots(): IslandPlot[] {
  const groups = new Map<string, SubgridCoord[]>();
  for (const coord of canvasSubcells()) {
    const id = plotIdForSubcell(coord);
    const list = groups.get(id) ?? [];
    list.push(coord);
    groups.set(id, list);
  }

  const seeds: Array<{ id: string; col: number; row: number; subcells: SubgridCoord[] }> = [];
  for (const [id, subcells] of groups) {
    const parsed = parsePlotId(id);
    if (!parsed) {
      continue;
    }
    seeds.push({ id, col: parsed.col, row: parsed.row, subcells });
  }

  const starter = seeds.filter((seed) => isStarterCell(seed.col, seed.row));
  const starterKeys = new Set(starter.map((seed) => `${seed.col},${seed.row}`));

  function ringOf(col: number, row: number): number {
    if (starterKeys.has(`${col},${row}`)) {
      return 0;
    }
    let best = Infinity;
    for (const seed of starter) {
      const dist = Math.abs(col - seed.col) + Math.abs(row - seed.row) / 2;
      if (dist < best) {
        best = dist;
      }
    }
    return best === Infinity ? 1 : Math.round(best);
  }

  const paid = seeds.filter((seed) => !starterKeys.has(`${seed.col},${seed.row}`));
  const paidScore = paid.length === 0 ? 0 : LAND_SCORE_BUDGET / paid.length;

  return seeds
    .map((seed) => {
      const points = seed.subcells.map((coord) => subcellWorldCenter(coord, SPEC));
      const xs = points.map((point) => point.x);
      const zs = points.map((point) => point.z);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minZ = Math.min(...zs);
      const maxZ = Math.max(...zs);
      const ring = ringOf(seed.col, seed.row);
      const startsUnlocked = ring === 0;
      const majority = seed.subcells[0] ? parcelForSubcell(seed.subcells[0]) : PARCEL_START;
      let cost = startsUnlocked ? 0 : costForRing(ring);
      if (!startsUnlocked && EAST_SLOT_PLOT_IDS.has(seed.id)) {
        cost = EAST_SLOT_PLOT_COST;
      }
      if (!startsUnlocked && NORTH_SLOT_PLOT_IDS.has(seed.id)) {
        cost = Math.max(cost, NORTH_SLOT_BASE_COST + Math.max(0, ring - 3) * 1500);
      }
      return {
        id: seed.id,
        col: seed.col,
        row: seed.row,
        legacyMacro: majority,
        startsUnlocked,
        ring,
        cost,
        parkScore: startsUnlocked ? 0 : paidScore,
        subcells: seed.subcells,
        center: { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 },
        width: maxX - minX + SPEC.subcellSize,
        depth: maxZ - minZ + SPEC.subcellSize,
      } satisfies IslandPlot;
    })
    .sort((a, b) => a.row - b.row || a.col - b.col);
}

export function islandPlots(): IslandPlot[] {
  if (!cachedPlots) {
    cachedPlots = buildPlots();
    cachedById = new Map(cachedPlots.map((plot) => [plot.id, plot]));
  }
  return cachedPlots;
}

export function plotById(id: string): IslandPlot | undefined {
  islandPlots();
  return cachedById?.get(id);
}

export function starterPlotIds(): string[] {
  return islandPlots().filter((plot) => plot.startsUnlocked).map((plot) => plot.id);
}

export function allPlotIds(): string[] {
  return islandPlots().map((plot) => plot.id);
}

/**
 * Map a park-plane world point to the atomic plot the player is pointing at.
 * Uses canvas/usable-grass membership first, then the plot lattice, then the
 * same axis-aligned footprint drawn as the Land-mode overlay. Distant
 * overlays stay raycast-disabled; this is the ground-pick purchase target.
 */
export function plotIdContainingWorld(x: number, z: number): string | null {
  const coord = worldToSubgrid(x, z, SPEC);
  const query = queryBuildableSubcell(coord, []);
  if (query.plotId && plotById(query.plotId)) {
    return query.plotId;
  }
  const latticeId = plotIdForSubcell(coord);
  if (plotById(latticeId)) {
    return latticeId;
  }
  let best: IslandPlot | null = null;
  let bestDist = Infinity;
  for (const plot of islandPlots()) {
    if (
      Math.abs(x - plot.center.x) <= plot.width / 2 + 1e-6 &&
      Math.abs(z - plot.center.z) <= plot.depth / 2 + 1e-6
    ) {
      const dist = Math.hypot(x - plot.center.x, z - plot.center.z);
      if (dist < bestDist) {
        bestDist = dist;
        best = plot;
      }
    }
  }
  return best?.id ?? null;
}

export function isKnownLandId(id: string): boolean {
  return id === PARCEL_START || id === PARCEL_EAST || id === PARCEL_NORTH || Boolean(plotById(id));
}

export function plotCost(plotId: string): number {
  return plotById(plotId)?.cost ?? 0;
}

export function plotParkScore(plotId: string): number {
  return plotById(plotId)?.parkScore ?? 0;
}

export function plotLabel(plotId: string): string {
  const plot = plotById(plotId);
  if (!plot) {
    return plotId;
  }
  if (plot.startsUnlocked) {
    return 'Park lawn';
  }
  if (plot.legacyMacro === PARCEL_EAST) {
    return plot.ring <= 1 ? 'East lawn' : 'East ridge';
  }
  if (plot.legacyMacro === PARCEL_NORTH) {
    return plot.ring >= 3 ? 'North ridge' : 'North lawn';
  }
  return plot.ring <= 1 ? 'Nearby lawn' : 'Outer lawn';
}

export function plotsForLegacyMacro(
  macro: typeof PARCEL_START | typeof PARCEL_EAST | typeof PARCEL_NORTH,
): IslandPlot[] {
  return islandPlots().filter((plot) => plot.legacyMacro === macro);
}

export function landOwnsPlot(unlockedLandIds: readonly string[], plotId: string): boolean {
  if (unlockedLandIds.includes(plotId)) {
    return true;
  }
  const plot = plotById(plotId);
  if (!plot) {
    return false;
  }
  if (unlockedLandIds.includes(plot.legacyMacro)) {
    return true;
  }
  const hasStart = unlockedLandIds.includes(PARCEL_START);
  const hasEast = unlockedLandIds.includes(PARCEL_EAST);
  const hasNorth = unlockedLandIds.includes(PARCEL_NORTH);
  if (hasStart && hasEast && hasNorth) {
    return true;
  }
  return false;
}

export function hasMacroEntitlement(
  unlockedLandIds: readonly string[],
  macro: string,
): boolean {
  if (unlockedLandIds.includes(macro)) {
    return true;
  }
  if (macro === PARCEL_START) {
    return islandPlots().some((plot) => plot.startsUnlocked && landOwnsPlot(unlockedLandIds, plot.id));
  }
  if (macro === PARCEL_EAST) {
    return EAST_MACRO_ANCHOR_PLOT_IDS.every((id) => landOwnsPlot(unlockedLandIds, id));
  }
  if (macro === PARCEL_NORTH) {
    return [...NORTH_SLOT_PLOT_IDS].some((id) => landOwnsPlot(unlockedLandIds, id));
  }
  return islandPlots().some(
    (plot) => plot.legacyMacro === macro && !plot.startsUnlocked && landOwnsPlot(unlockedLandIds, plot.id),
  );
}

export function missingMacroAnchorPlots(
  unlockedLandIds: readonly string[],
  macro: string,
): IslandPlot[] {
  if (hasMacroEntitlement(unlockedLandIds, macro)) {
    return [];
  }
  if (macro === PARCEL_EAST) {
    return EAST_MACRO_ANCHOR_PLOT_IDS.map((id) => plotById(id)).filter(
      (plot): plot is IslandPlot => plot != null && !landOwnsPlot(unlockedLandIds, plot.id),
    );
  }
  if (macro === PARCEL_NORTH) {
    return [...NORTH_SLOT_PLOT_IDS]
      .map((id) => plotById(id))
      .filter((plot): plot is IslandPlot => plot != null && !landOwnsPlot(unlockedLandIds, plot.id))
      .sort((a, b) => a.cost - b.cost)
      .slice(0, 1);
  }
  return [];
}

/** Remaining coin cost of the plots that still gate this macro entitlement. */
export function remainingMacroEntitlementCost(
  unlockedLandIds: readonly string[],
  macro: string,
): number {
  return missingMacroAnchorPlots(unlockedLandIds, macro).reduce((sum, plot) => sum + plot.cost, 0);
}

export function plotNeighbors(plotId: string): IslandPlot[] {
  const plot = plotById(plotId);
  if (!plot) {
    return [];
  }
  const deltas: Array<[number, number]> = [
    [1, 0],
    [-1, 0],
    [0, 2],
    [0, -2],
  ];
  return deltas
    .map(([dc, dr]) => plotById(plotIdForLegacyCell(plot.col + dc, plot.row + dr)))
    .filter((item): item is IslandPlot => Boolean(item));
}

export function isPlotAdjacentToUnlocked(plotId: string, unlockedLandIds: readonly string[]): boolean {
  return plotNeighbors(plotId).some((neighbor) => landOwnsPlot(unlockedLandIds, neighbor.id));
}

/** Locked plots that share an edge with currently owned land. */
export function purchasableFrontierPlots(unlockedLandIds: readonly string[]): IslandPlot[] {
  return islandPlots().filter(
    (plot) =>
      !plot.startsUnlocked &&
      !landOwnsPlot(unlockedLandIds, plot.id) &&
      isPlotAdjacentToUnlocked(plot.id, unlockedLandIds),
  );
}

/**
 * V2 macro parcels → V3 atomic plots. Idempotent: plot IDs pass through.
 * start-only → starter cluster
 * +east → former east spatial entitlement
 * +north / all three → the complete currently entitled island
 */
export function migrateLegacyLandIds(unlockedLandIds: readonly string[]): string[] {
  const existingPlots = unlockedLandIds.filter((id) => isPlotId(id) && plotById(id));
  const hasMacros = unlockedLandIds.some(
    (id) => id === PARCEL_START || id === PARCEL_EAST || id === PARCEL_NORTH,
  );
  if (existingPlots.length > 0 && !hasMacros) {
    const next = [...existingPlots];
    for (const id of starterPlotIds()) {
      if (!next.includes(id)) {
        next.push(id);
      }
    }
    return next;
  }

  const hasEast = unlockedLandIds.includes(PARCEL_EAST);
  const hasNorth = unlockedLandIds.includes(PARCEL_NORTH);
  if (hasNorth) {
    return allPlotIds();
  }

  const next = [...starterPlotIds()];
  if (hasEast) {
    for (const plot of plotsForLegacyMacro(PARCEL_EAST)) {
      if (!next.includes(plot.id)) {
        next.push(plot.id);
      }
    }
  }
  return next;
}

export function expansionCameraTier(unlockedLandIds: readonly string[]): number {
  if (hasMacroEntitlement(unlockedLandIds, PARCEL_NORTH)) {
    return 3;
  }
  if (hasMacroEntitlement(unlockedLandIds, PARCEL_EAST) || unlockedLandIds.some((id) => isPlotId(id) && !starterPlotIds().includes(id))) {
    return 2;
  }
  return 1;
}

/** Documented seam: grant one plot from a future authorized source without changing IDs. */
export function grantPlotOwnership(unlockedLandIds: string[], plotId: string, _source: PlotUnlockSource = 'coins'): string[] {
  if (!plotById(plotId) || unlockedLandIds.includes(plotId)) {
    return unlockedLandIds;
  }
  return [...unlockedLandIds, plotId];
}

export function plotWorldCenter(plotId: string): WorldPoint {
  const plot = plotById(plotId);
  if (plot) {
    return plot.center;
  }
  const parsed = parsePlotId(plotId);
  if (parsed) {
    return legacyGridToWorld(parsed.col, parsed.row);
  }
  return { x: 0, z: 0 };
}
